import * as Dialog from '@radix-ui/react-dialog';
import type { HTMLAttributes } from 'react';
import { forwardRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ComponentProps } from '~/chords/ComponentProps';
import { getClassNameOverride } from '~/chords/getClassNameOverride';
import { IconButton } from '~/chords/IconButton';
import { cn } from '~/chords/lib/cn';
import { ModalDialogClose } from '~/chords/ModalDialog/components/ModalDialogClose';

export interface ModalDialogHeaderProps
  extends Omit<ComponentProps<HTMLAttributes<HTMLDivElement>>, 'className'> {
  children: ReactNode;
  showCloseButton?: boolean;
  asChild?: boolean;
}

export const ModalDialogHeader = forwardRef<
  HTMLDivElement,
  ModalDialogHeaderProps
>(({ children, showCloseButton = true, asChild, ...props }, ref) => {
  const { t } = useTranslation('common');
  const className = getClassNameOverride(props, 'ModalDialog.Header');

  return (
    <div
      ref={ref}
      {...props}
      className={cn(
        {
          'flex items-start justify-between gap-sm px-xl pb-sm pt-lg': !asChild,
        },
        className,
      )}
    >
      <Dialog.Title
        asChild={asChild}
        className={cn({
          'text-headline-md font-semibold leading-tight': !asChild,
        })}
      >
        {children}
      </Dialog.Title>
      {!asChild && showCloseButton ? (
        <div className="-my-md -mr-lg">
          <ModalDialogClose>
            <IconButton
              type="button"
              iconType="xmark"
              iconVariant="regular"
              size="large"
              aria-label={t('closeModal')}
            />
          </ModalDialogClose>
        </div>
      ) : null}
    </div>
  );
});

ModalDialogHeader.displayName = 'ModalDialog.Header';
